import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Settings, Edit, RefreshCw, Search, X, ChevronLeft, AlertTriangle, LinkIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ImageUploader } from "./ImageUploader";
import { TechnicalSpecsInput } from "./TechnicalSpecsInput";
import { supabase } from "@/lib/supabase";

// Definición de interfaces
interface SparePart {
  id: number;
  name: string;
  description: string;
  price: string;
  images: string[];
  sku: string | null;
  inStock: boolean;
  manufacturer: string | null;
  technicalSpecs: Array<{ label: string; value: string }>;
  createdAt: string;
  updatedAt: string;
}

interface Product {
  id: number;
  name: string;
}

interface ProductSparePart {
  id: number;
  productId: number;
  sparePartId: number;
  compatibilityNotes: string | null;
  isEssential: boolean;
  productName?: string;
  sparePartName?: string;
}

// Componente principal
export function SparePartsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Estados
  const [searchQuery, setSearchQuery] = useState("");
  const [showingNewSparePartForm, setShowingNewSparePartForm] = useState(false);
  const [editingSparePart, setEditingSparePart] = useState<SparePart | null>(null);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [selectedSparePartId, setSelectedSparePartId] = useState<number | null>(null);

  // Estados para confirmación de eliminación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sparePartToDelete, setSparePartToDelete] = useState<number | null>(null);
  const [sparePartNameToDelete, setSparePartNameToDelete] = useState<string>("");

  // Estados para asignación de productos
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [compatibilityNotes, setCompatibilityNotes] = useState("");
  const [isEssential, setIsEssential] = useState(false);
  const [showAssignments, setShowAssignments] = useState(false);
  const [selectedSparePartForAssignments, setSelectedSparePartForAssignments] = useState<SparePart | null>(null);

  // Nuevo estado para producto obligatorio al crear un recambio
  const [associatedProductId, setAssociatedProductId] = useState<number | null>(null);
  const [associatedProductNotes, setAssociatedProductNotes] = useState("");
  const [isAssociatedProductEssential, setIsAssociatedProductEssential] = useState(true);

  // Estado inicial para nuevos recambios
  const initialNewSparePartState: Partial<SparePart> = {
    name: "",
    description: "",
    price: "",
    images: [],
    sku: "",
    inStock: true,
    manufacturer: "",
    technicalSpecs: []
  };

  const [newSparePart, setNewSparePart] = useState<Partial<SparePart>>(initialNewSparePartState);

  // Queries
  const {
    data: spareParts = [],
    isLoading: isLoadingSpareParts,
    refetch: refetchSpareParts
  } = useQuery({
    queryKey: ["spareParts"],
    queryFn: () => adminApi.spareParts.getAll(),
    enabled: !showingNewSparePartForm
  });

  const {
    data: products = [],
    isLoading: isLoadingProducts,
    error: productsError,
    refetch: refetchProducts
  } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      console.log("Ejecutando consulta de productos");
      try {
        const result = await adminApi.products.getAll();
        console.log("Productos cargados:", result.length, result);
        return result;
      } catch (error) {
        console.error("Error al cargar productos:", error);
        throw error;
      }
    },
    // Siempre cargar los productos, independientemente del estado
    enabled: true
  });

  const {
    data: productSpareParts = [],
    isLoading: isLoadingProductSpareParts
  } = useQuery({
    queryKey: ["productSpareParts", selectedSparePartForAssignments?.id],
    queryFn: () => adminApi.spareParts.getAssignments(selectedSparePartForAssignments!.id),
    enabled: !!selectedSparePartForAssignments && showAssignments
  });

  // Efecto para verificar el estado de la carga de productos
  useEffect(() => {
    if (productsError) {
      console.error("Error en la consulta de productos:", productsError);
      toast({
        title: "Error al cargar productos",
        description: "No se pudieron cargar los productos para asociar con recambios.",
        variant: "destructive"
      });
    }

    if (showingNewSparePartForm) {
      console.log("Form de nuevo recambio visible, estado de productos:", {
        isLoading: isLoadingProducts,
        productsLength: products?.length || 0,
        hasError: !!productsError
      });

      // Si hay error o no hay productos, intentar cargar de nuevo
      if ((productsError || products.length === 0) && !isLoadingProducts) {
        console.log("Intentando recargar productos...");
        refetchProducts();
      }
    }
  }, [showingNewSparePartForm, productsError, products.length, isLoadingProducts, refetchProducts]);

  // Mutations
  const deleteSparePartMutation = useMutation({
    mutationFn: (id: number) => adminApi.spareParts.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spareParts"] });
      toast({
        title: "Recambio eliminado",
        description: "El recambio ha sido eliminado correctamente."
      });
      setShowDeleteModal(false);
      setSparePartToDelete(null);
      setSparePartNameToDelete("");
    },
    onError: (error: any) => {
      toast({
        title: "Error al eliminar",
        description: error.message,
        variant: "destructive"
      });
      setShowDeleteModal(false);
      setSparePartToDelete(null);
      setSparePartNameToDelete("");
    }
  });

  const saveSparePartMutation = useMutation({
    mutationFn: async (sparePart: Partial<SparePart>) => {
      // Primero creamos o actualizamos el recambio
      let result;
      if (editingSparePart) {
        result = await adminApi.spareParts.update(editingSparePart.id, sparePart);
      } else {
        result = await adminApi.spareParts.create(sparePart);
      }

      // Si se ha especificado un producto asociado y es un nuevo recambio, creamos la asignación
      if (associatedProductId && !editingSparePart && result.id) {
        await adminApi.spareParts.assignToProduct({
          sparePartId: result.id,
          productId: associatedProductId,
          compatibilityNotes: associatedProductNotes,
          isEssential: isAssociatedProductEssential
        });
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["spareParts"] });
      setEditingSparePart(null);
      setNewSparePart(initialNewSparePartState);
      setAssociatedProductId(null);
      setAssociatedProductNotes("");
      setIsAssociatedProductEssential(true);
      toast({
        title: editingSparePart ? "Recambio actualizado" : "Recambio creado",
        description: editingSparePart
          ? "El recambio ha sido actualizado correctamente."
          : "El recambio ha sido creado correctamente."
      });
      setShowingNewSparePartForm(false);
      refetchSpareParts();
    },
    onError: (error: any) => {
      toast({
        title: "Error al guardar",
        description: error.message || "Ocurrió un error al guardar el recambio.",
        variant: "destructive"
      });
      console.error("Error completo:", error);
    }
  });

  const assignToProductMutation = useMutation({
    mutationFn: (data: { sparePartId: number; productId: number; compatibilityNotes: string; isEssential: boolean }) => {
      return adminApi.spareParts.assignToProduct(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["productSpareParts"] });
      toast({
        title: "Asignación realizada",
        description: "El recambio ha sido asignado correctamente a la máquina."
      });
      // Resetear el formulario
      setSelectedProductId(null);
      setCompatibilityNotes("");
      setIsEssential(false);
      setShowAssignForm(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error en la asignación",
        description: error.message || "Ocurrió un error al asignar el recambio.",
        variant: "destructive"
      });
      console.error("Error completo:", error);
    }
  });

  const removeAssignmentMutation = useMutation({
    mutationFn: (assignmentId: number) => {
      return adminApi.spareParts.removeAssignment(assignmentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["productSpareParts"] });
      toast({
        title: "Asignación eliminada",
        description: "La asignación ha sido eliminada correctamente."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error al eliminar asignación",
        description: error.message || "Ocurrió un error al eliminar la asignación.",
        variant: "destructive"
      });
      console.error("Error completo:", error);
    }
  });

  // Handlers
  const handleEditSparePart = (sparePart: SparePart) => {
    setEditingSparePart(sparePart);
    setNewSparePart({
      ...sparePart,
      technicalSpecs: sparePart.technicalSpecs || []
    });
    setShowingNewSparePartForm(true);
  };

  const handleDeleteSparePart = (sparePartId: number, sparePartName: string) => {
    setSparePartToDelete(sparePartId);
    setSparePartNameToDelete(sparePartName);
    setShowDeleteModal(true);
  };

  const confirmDeleteSparePart = () => {
    if (sparePartToDelete !== null) {
      deleteSparePartMutation.mutate(sparePartToDelete);
    }
  };

  const handleSaveSparePart = async () => {
    if (!newSparePart.name?.trim()) {
      toast({
        title: "Error",
        description: "El nombre del recambio es obligatorio",
        variant: "destructive"
      });
      return;
    }

    if (!newSparePart.description?.trim()) {
      toast({
        title: "Error",
        description: "La descripción del recambio es obligatoria",
        variant: "destructive"
      });
      return;
    }

    if (!newSparePart.price?.trim()) {
      toast({
        title: "Error",
        description: "El precio del recambio es obligatorio",
        variant: "destructive"
      });
      return;
    }

    // Si es un nuevo recambio, verificar que se haya seleccionado un producto
    if (!editingSparePart && !associatedProductId) {
      toast({
        title: "Error",
        description: "Debes asociar el recambio a al menos un producto",
        variant: "destructive"
      });
      return;
    }

    try {
      await saveSparePartMutation.mutateAsync(newSparePart);
    } catch (error) {
      console.error("Error al guardar recambio:", error);
    }
  };

  const handleShowAssignForm = (sparePartId: number) => {
    setSelectedSparePartId(sparePartId);
    setShowAssignForm(true);
  };

  const handleAssignToProduct = () => {
    if (!selectedSparePartId || !selectedProductId) {
      toast({
        title: "Error",
        description: "Selecciona un producto para asignar",
        variant: "destructive"
      });
      return;
    }

    assignToProductMutation.mutate({
      sparePartId: selectedSparePartId,
      productId: selectedProductId,
      compatibilityNotes: compatibilityNotes,
      isEssential: isEssential
    });
  };

  const handleShowAssignments = (sparePart: SparePart) => {
    setSelectedSparePartForAssignments(sparePart);
    setShowAssignments(true);
  };

  const resetForm = () => {
    setShowingNewSparePartForm(false);
    setEditingSparePart(null);
    setNewSparePart(initialNewSparePartState);
    setAssociatedProductId(null);
    setAssociatedProductNotes("");
    setIsAssociatedProductEssential(true);
  };

  const filteredSpareParts = spareParts.filter(part =>
    part.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    part.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    part.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    part.sku?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoadingSpareParts && !showingNewSparePartForm) {
    return <div>Cargando recambios...</div>;
  }

  // Vista del formulario
  if (showingNewSparePartForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6 bg-zinc-900/70 p-4 rounded-xl border border-zinc-800/50 shadow-lg">
          <button
            className="px-4 py-2 text-sm border border-zinc-700 rounded-lg flex items-center gap-2 text-white bg-zinc-800 hover:bg-zinc-700 transition-colors"
            onClick={resetForm}
          >
            <ChevronLeft className="h-4 w-4" /> Volver
          </button>
          <h2 className="text-xl font-semibold text-white flex-1">
            {editingSparePart ? `Editar Recambio: ${editingSparePart.name}` : "Crear Nuevo Recambio"}
          </h2>
        </div>

        <Card className="border-zinc-800/50 bg-zinc-900/70 shadow-xl overflow-hidden">
          <form onSubmit={(e) => { e.preventDefault(); handleSaveSparePart(); }} className="divide-y divide-zinc-800/30">
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Información básica */}
                <div className="space-y-6">
                  <div className="border-l-4 border-[#FCAA0D] pl-4 mb-6">
                    <h3 className="text-lg font-bold text-white mb-1">Información Básica</h3>
                    <p className="text-zinc-400 text-sm">Datos principales del recambio</p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white flex items-center">
                        Nombre <span className="text-[#FCAA0D] ml-1">*</span>
                      </Label>
                      <Input
                        id="name"
                        value={newSparePart.name || ""}
                        onChange={(e) => setNewSparePart({...newSparePart, name: e.target.value})}
                        placeholder="Nombre del recambio"
                        className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description" className="text-white">
                        Descripción <span className="text-[#FCAA0D] ml-1">*</span>
                      </Label>
                      <Textarea
                        id="description"
                        value={newSparePart.description || ""}
                        onChange={(e) => setNewSparePart({...newSparePart, description: e.target.value})}
                        placeholder="Descripción detallada del recambio"
                        className="min-h-[120px] bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="sku" className="text-white">SKU / Referencia</Label>
                        <Input
                          id="sku"
                          value={newSparePart.sku || ""}
                          onChange={(e) => setNewSparePart({...newSparePart, sku: e.target.value})}
                          placeholder="SKU del recambio"
                          className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="price" className="text-white">
                          Precio <span className="text-[#FCAA0D] ml-1">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id="price"
                            value={newSparePart.price || ""}
                            onChange={(e) => setNewSparePart({...newSparePart, price: e.target.value})}
                            placeholder="Ej. 120€, Consultar, etc."
                            className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 pl-8"
                          />
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400">€</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="manufacturer" className="text-white">Fabricante</Label>
                      <Input
                        id="manufacturer"
                        value={newSparePart.manufacturer || ""}
                        onChange={(e) => setNewSparePart({...newSparePart, manufacturer: e.target.value})}
                        placeholder="Fabricante del recambio"
                        className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="in_stock"
                        checked={newSparePart.inStock || false}
                        onChange={(e) => setNewSparePart({...newSparePart, inStock: e.target.checked})}
                        className="rounded text-[#FCAA0D] focus:ring-[#FCAA0D]"
                      />
                      <Label htmlFor="in_stock" className="text-white">Disponible en stock</Label>
                    </div>
                  </div>
                </div>

                {/* Imágenes y especificaciones */}
                <div className="space-y-6">
                  <div className="border-l-4 border-[#FCAA0D] pl-4 mb-6">
                    <h3 className="text-lg font-bold text-white mb-1">Imágenes y Especificaciones</h3>
                    <p className="text-zinc-400 text-sm">Media y datos técnicos</p>
                  </div>

                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label className="text-white">Imágenes</Label>
                      <ImageUploader
                        images={newSparePart.images || []}
                        onChange={(images) => setNewSparePart({...newSparePart, images})}
                        folder="spare-parts"
                      />
                    </div>

                    <div className="space-y-2">
                      <TechnicalSpecsInput
                        value={newSparePart.technicalSpecs || []}
                        onChange={(technicalSpecs) => setNewSparePart({...newSparePart, technicalSpecs})}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Asociación de producto obligatorio para nuevos recambios */}
              {!editingSparePart && (
                <div className="mt-8 pt-6 border-t border-zinc-800/30">
                  <div className="border-l-4 border-[#FCAA0D] pl-4 mb-6">
                    <h3 className="text-lg font-bold text-white mb-1">Asociación con Producto <span className="text-[#FCAA0D] ml-1">*</span></h3>
                    <p className="text-zinc-400 text-sm">Todo recambio debe estar asociado a al menos un producto</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="associated-product" className="text-white">
                        Producto compatible <span className="text-[#FCAA0D] ml-1">*</span>
                      </Label>

                      {isLoadingProducts ? (
                        <div className="p-2 bg-zinc-800/50 border border-zinc-700 rounded-md text-zinc-400 flex items-center gap-2">
                          <RefreshCw className="h-4 w-4 animate-spin" /> Cargando productos...
                        </div>
                      ) : productsError ? (
                        <div className="p-2 bg-red-900/20 border border-red-700/30 rounded-md text-red-400 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" /> Error al cargar productos
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => refetchProducts()}
                            className="ml-auto text-white text-xs px-2 py-1 h-auto"
                          >
                            Reintentar
                          </Button>
                        </div>
                      ) : products.length === 0 ? (
                        <div className="p-2 bg-amber-900/20 border border-amber-700/30 rounded-md text-amber-400 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" /> No hay productos disponibles
                        </div>
                      ) : (
                        <select
                          id="associated-product"
                          value={associatedProductId?.toString() || ""}
                          onChange={(e) => setAssociatedProductId(e.target.value ? Number(e.target.value) : null)}
                          className="w-full p-2 rounded-md bg-zinc-800 border border-zinc-700 text-white"
                        >
                          <option value="">Seleccionar producto...</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id.toString()}>
                              {product.name}
                            </option>
                          ))}
                        </select>
                      )}

                      {/* Mostrar el número de productos cargados para depuración */}
                      <p className="text-xs text-zinc-500">
                        {!isLoadingProducts && !productsError && products.length > 0
                          ? `${products.length} productos disponibles`
                          : ""}
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="associated-product-notes" className="text-white">Notas de compatibilidad</Label>
                        <Input
                          id="associated-product-notes"
                          value={associatedProductNotes}
                          onChange={(e) => setAssociatedProductNotes(e.target.value)}
                          placeholder="Detalles sobre la compatibilidad..."
                          className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                          disabled={isLoadingProducts || !!productsError || products.length === 0}
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="is-essential"
                          checked={isAssociatedProductEssential}
                          onChange={(e) => setIsAssociatedProductEssential(e.target.checked)}
                          className="rounded text-[#FCAA0D] focus:ring-[#FCAA0D]"
                          disabled={isLoadingProducts || !!productsError || products.length === 0}
                        />
                        <Label htmlFor="is-essential" className="text-white">Recambio esencial / recomendado</Label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="border-zinc-700 text-white hover:bg-zinc-800"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black"
                disabled={!editingSparePart && (isLoadingProducts || products.length === 0)}
              >
                {editingSparePart ? "Actualizar Recambio" : "Crear Recambio"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    );
  }

  // Vista de formulario de asignación
  if (showAssignForm) {
    const sparePart = spareParts.find(p => p.id === selectedSparePartId);

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6 bg-zinc-900/70 p-4 rounded-xl border border-zinc-800/50 shadow-lg">
          <button
            className="px-4 py-2 text-sm border border-zinc-700 rounded-lg flex items-center gap-2 text-white bg-zinc-800 hover:bg-zinc-700 transition-colors"
            onClick={() => setShowAssignForm(false)}
          >
            <ChevronLeft className="h-4 w-4" /> Volver
          </button>
          <h2 className="text-xl font-semibold text-white flex-1">
            Asignar recambio a una máquina
          </h2>
        </div>

        <Card className="border-zinc-800/50 bg-zinc-900/70 shadow-xl">
          <CardHeader>
            <CardTitle className="text-lg text-white">
              Asignar: {sparePart?.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="product" className="text-white">Seleccionar máquina</Label>
              <select
                id="product"
                value={selectedProductId?.toString() || ""}
                onChange={(e) => setSelectedProductId(e.target.value ? Number(e.target.value) : null)}
                className="w-full p-2 rounded-md bg-zinc-800 border border-zinc-700 text-white"
              >
                <option value="">Seleccionar máquina...</option>
                {products.map(product => (
                  <option key={product.id} value={product.id.toString()}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="compatibilityNotes" className="text-white">Notas de compatibilidad</Label>
              <Textarea
                id="compatibilityNotes"
                value={compatibilityNotes}
                onChange={(e) => setCompatibilityNotes(e.target.value)}
                placeholder="Notas sobre la compatibilidad o detalles específicos..."
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isEssential"
                checked={isEssential}
                onChange={(e) => setIsEssential(e.target.checked)}
                className="rounded text-[#FCAA0D] focus:ring-[#FCAA0D]"
              />
              <Label htmlFor="isEssential" className="text-white">Recambio esencial / recomendado</Label>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAssignForm(false)}
                className="border-zinc-700 text-white hover:bg-zinc-800"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleAssignToProduct}
                className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black"
              >
                Asignar a Máquina
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Vista de asignaciones de un recambio
  if (showAssignments && selectedSparePartForAssignments) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-6 bg-zinc-900/70 p-4 rounded-xl border border-zinc-800/50 shadow-lg">
          <button
            className="px-4 py-2 text-sm border border-zinc-700 rounded-lg flex items-center gap-2 text-white bg-zinc-800 hover:bg-zinc-700 transition-colors"
            onClick={() => {
              setShowAssignments(false);
              setSelectedSparePartForAssignments(null);
            }}
          >
            <ChevronLeft className="h-4 w-4" /> Volver
          </button>
          <h2 className="text-xl font-semibold text-white flex-1">
            Máquinas asignadas al recambio: {selectedSparePartForAssignments.name}
          </h2>
        </div>

        <Card className="border-zinc-800/50 bg-zinc-900/70 shadow-xl">
          <CardContent className="p-6">
            {isLoadingProductSpareParts ? (
              <div className="text-center py-8 text-zinc-400">Cargando asignaciones...</div>
            ) : productSpareParts.length === 0 ? (
              <div className="text-center py-8 text-zinc-400">
                <p className="mb-4">Este recambio no está asignado a ninguna máquina.</p>
                <Button
                  onClick={() => {
                    setSelectedSparePartId(selectedSparePartForAssignments.id);
                    setShowAssignments(false);
                    setShowAssignForm(true);
                  }}
                  className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black"
                >
                  Asignar a una máquina
                </Button>
              </div>
            ) : (
              <>
                <div className="mb-4 flex justify-between items-center">
                  <h3 className="text-white font-semibold">Máquinas con este recambio</h3>
                  <Button
                    onClick={() => {
                      setSelectedSparePartId(selectedSparePartForAssignments.id);
                      setShowAssignments(false);
                      setShowAssignForm(true);
                    }}
                    size="sm"
                    className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Asignar Otra
                  </Button>
                </div>
                <Table>
                  <TableHeader className="bg-zinc-800/50">
                    <TableRow>
                      <TableHead className="text-zinc-400">Máquina</TableHead>
                      <TableHead className="text-zinc-400">Notas de compatibilidad</TableHead>
                      <TableHead className="text-zinc-400">Esencial</TableHead>
                      <TableHead className="text-zinc-400 text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productSpareParts.map((assignment) => (
                      <TableRow key={assignment.id} className="border-zinc-800/30">
                        <TableCell className="text-white">{assignment.productName}</TableCell>
                        <TableCell className="text-zinc-300">
                          {assignment.compatibilityNotes || "—"}
                        </TableCell>
                        <TableCell>
                          {assignment.isEssential ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                              Esencial
                            </Badge>
                          ) : (
                            <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30">
                              Opcional
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeAssignmentMutation.mutate(assignment.id)}
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Vista principal de la lista de recambios
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 mb-6 bg-zinc-900/70 p-4 rounded-xl border border-zinc-800/50 shadow-lg">
        <h2 className="text-xl font-semibold text-white">Gestión de Recambios</h2>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              // Mostrar el token actual para depuración
              const token = localStorage.getItem("adminToken");
              console.log("Token actual:", token ? token.substring(0, 15) + "..." : "No hay token");

              // Intentar iniciar sesión de nuevo
              const doLogin = async () => {
                try {
                  toast({
                    title: "Renovando autenticación...",
                    description: "Intentando generar un nuevo token",
                  });

                  // Credenciales de administrador
                  const response = await fetch("https://jlufgkgssnxumguvknhc.supabase.co/functions/v1/admin/login", {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                      username: "innotec1",
                      password: "bizerba123"
                    })
                  });

                  if (!response.ok) {
                    throw new Error(`Error al iniciar sesión: ${response.status}`);
                  }

                  const data = await response.json();

                  if (data.token) {
                    // Guardar el nuevo token
                    localStorage.setItem("adminToken", data.token);
                    console.log("Nuevo token generado y guardado");

                    toast({
                      title: "Autenticación renovada",
                      description: "Se ha generado un nuevo token. Intentando cargar datos...",
                    });

                    // Refrescar datos con el nuevo token
                    await refetchProducts();
                    await refetchSpareParts();
                  } else {
                    throw new Error("No se recibió un token válido");
                  }
                } catch (error) {
                  console.error("Error al renovar la autenticación:", error);
                  toast({
                    title: "Error de autenticación",
                    description: error.message || "No se pudo generar un nuevo token",
                    variant: "destructive"
                  });
                }
              };

              doLogin();
            }}
            variant="destructive"
            className="border-red-700 text-white hover:bg-red-800"
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Renovar autenticación
          </Button>

          <Button
            onClick={() => {
              // Verificar el token y la conexión
              const token = localStorage.getItem("adminToken");
              console.log("Token actual:", token ? "Presente" : "No hay token");

              // Intentar refrescar productos
              toast({
                title: "Verificando conexión",
                description: "Comprobando autenticación y refrescando datos...",
              });

              // Forzar recarga de productos
              refetchProducts().then(
                (result) => {
                  console.log("Recarga exitosa:", result.data?.length || 0, "productos");
                  toast({
                    title: "Conexión establecida",
                    description: `Se cargaron ${result.data?.length || 0} productos correctamente.`
                  });
                },
                (error) => {
                  console.error("Error al recargar:", error);
                  toast({
                    title: "Error de conexión",
                    description: error.message || "No se pudieron cargar los productos",
                    variant: "destructive"
                  });
                }
              );
            }}
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-800"
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Verificar conexión
          </Button>
          <Button
            onClick={() => setShowingNewSparePartForm(true)}
            className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black"
          >
            <Plus className="mr-2 h-4 w-4" /> Crear Recambio
          </Button>
        </div>
      </div>

      {/* Mostrar indicador de error si hay problemas de autenticación */}
      {productsError && (
        <div className="p-4 mb-4 bg-red-900/20 border border-red-700/30 rounded-md text-red-400 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          <div className="flex-1">
            <p className="font-semibold">Error de autenticación detectado</p>
            <p className="text-sm">{productsError.message}</p>
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              localStorage.removeItem("adminToken");
              window.location.href = "/admin";
            }}
            className="ml-auto text-white text-xs px-2 py-1 h-auto"
          >
            Ir al login
          </Button>
        </div>
      )}

      {/* Búsqueda */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-500 h-4 w-4" />
        <Input
          placeholder="Buscar recambios..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-zinc-800/50 border-zinc-700 text-white focus:border-[#FCAA0D]/50 focus:ring-[#FCAA0D]/20"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Tabla de recambios */}
      {filteredSpareParts.length === 0 ? (
        <div className="bg-zinc-800/30 border border-zinc-700/30 rounded-lg p-12 text-center">
          <Settings className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
          <h3 className="text-xl font-semibold text-zinc-300 mb-2">No hay recambios disponibles</h3>
          <p className="text-zinc-500 max-w-md mx-auto mb-6">
            {searchQuery
              ? "No se encontraron recambios con los criterios de búsqueda."
              : "No hay recambios creados. Crea tu primer recambio para empezar."}
          </p>
          {searchQuery ? (
            <Button
              variant="outline"
              onClick={() => setSearchQuery("")}
              className="border-zinc-700 text-white hover:bg-zinc-800"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Limpiar búsqueda
            </Button>
          ) : (
            <Button
              onClick={() => setShowingNewSparePartForm(true)}
              className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black"
            >
              <Plus className="mr-2 h-4 w-4" /> Crear Recambio
            </Button>
          )}
        </div>
      ) : (
        <Card className="border-zinc-800/50 bg-zinc-900/70 shadow-xl overflow-hidden">
          <Table>
            <TableHeader className="bg-zinc-800/50">
              <TableRow>
                <TableHead className="text-zinc-400">Recambio</TableHead>
                <TableHead className="text-zinc-400">SKU</TableHead>
                <TableHead className="text-zinc-400">Fabricante</TableHead>
                <TableHead className="text-zinc-400">Precio</TableHead>
                <TableHead className="text-zinc-400">Estado</TableHead>
                <TableHead className="text-zinc-400">Asignaciones</TableHead>
                <TableHead className="text-zinc-400 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSpareParts.map((sparePart) => (
                <TableRow key={sparePart.id} className="border-zinc-800/30">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {sparePart.images && sparePart.images.length > 0 ? (
                        <img
                          src={sparePart.images[0]}
                          alt={sparePart.name}
                          className="w-10 h-10 rounded-md object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = "/placeholder-image.png";
                          }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-zinc-800 flex items-center justify-center">
                          <Settings className="w-5 h-5 text-zinc-500" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-white">{sparePart.name}</div>
                        <div className="text-xs text-zinc-500 line-clamp-1 max-w-xs">
                          {sparePart.description}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-zinc-300">
                    {sparePart.sku || "—"}
                  </TableCell>
                  <TableCell className="text-zinc-300">
                    {sparePart.manufacturer || "—"}
                  </TableCell>
                  <TableCell className="text-zinc-300">
                    {sparePart.price}
                  </TableCell>
                  <TableCell>
                    {sparePart.inStock ? (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                        En stock
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        Sin stock
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleShowAssignments(sparePart)}
                      className="text-[#FCAA0D] hover:text-[#FCAA0D] hover:bg-[#FCAA0D]/10"
                    >
                      <LinkIcon className="h-4 w-4 mr-1" /> Asignaciones
                    </Button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleShowAssignForm(sparePart.id)}
                        className="text-[#FCAA0D] hover:text-[#FCAA0D] hover:bg-[#FCAA0D]/10"
                      >
                        <LinkIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditSparePart(sparePart)}
                        className="text-blue-500 hover:text-blue-400 hover:bg-blue-500/10"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteSparePart(sparePart.id, sparePart.name)}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-zinc-900 rounded-lg p-6 max-w-md w-full">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl text-white font-bold text-center mb-2">Confirmar Eliminación</h3>
            <p className="text-zinc-400 text-center mb-6">
              ¿Estás seguro de que deseas eliminar el recambio <span className="text-white font-medium">"{sparePartNameToDelete}"</span>? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                className="border-zinc-700 text-white hover:bg-zinc-800"
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteSparePart}
              >
                Eliminar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
